<template>
  <div class="space-y-6">
    <!-- 헤더 -->
    <div class="card-base p-6">
      <div class="page-header mb-6">
        <div>
          <h2 class="page-title">발행사 관리</h2>
          <p class="page-subtitle">크레딧 채권 발행사를 관리합니다</p>
        </div>
      </div>

      <div class="page-header mb-6">
        <p class="text-sm text-gray-600 mt-1">총 {{ currentIssuers.length }}개 발행사</p>
      </div>

      <!-- 크레딧 타입 선택과 발행사 추가 버튼 -->
      <div class="mb-6 flex items-center justify-between">
        <div class="flex flex-wrap gap-2">
          <button v-for="(name, type) in creditTypes" :key="type" @click="currentCreditType = type" :class="[
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            currentCreditType === type
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          ]">
            {{ name }}
          </button>
        </div>

        <BaseButton variant="primary" icon="fas fa-plus" @click="handleOpenCreateModal">
          발행사 추가
        </BaseButton>
      </div>
      <!-- 로딩 상태 -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <div class="loading-spinner"></div>
        <span class="ml-3 text-gray-600">발행사 목록을 불러오는 중...</span>
      </div>

      <!-- 데이터 없음 -->
      <div v-else-if="currentIssuers.length === 0" class="text-center py-12 text-gray-500">
        <i class="fas fa-building text-4xl mb-4"></i>
        <p>등록된 발행사가 없습니다.</p>
        <p class="text-sm mt-2">새로운 발행사를 추가해보세요.</p>
      </div>

      <!-- 발행사 카드 그리드 -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <div v-for="issuer in currentIssuers" :key="issuer.id"
          class="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
          <!-- 카드 헤더 -->
          <div class="p-4">
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <div class="flex items-center space-x-2 mb-2">
                  <span :class="[
                    'px-2 py-1 rounded text-xs font-medium border',
                    getGradeColorClass(issuer.grade).bg,
                    getGradeColorClass(issuer.grade).text,
                    getGradeColorClass(issuer.grade).border
                  ]">
                    {{ issuer.grade }}
                  </span>
                </div>
                <h3 class="text-base font-medium text-gray-900 mb-2">
                  {{ issuer.name }}
                </h3>
                <div class="flex items-center text-sm text-gray-500">
                  <i class="fas fa-tags mr-1"></i>
                  <span>{{ issuer.bondAliases?.length || 0 }}개 별칭</span>
                </div>
              </div>

              <!-- 액션 버튼들 -->
              <div class="flex space-x-1">
                <button @click="handleOpenAddAliasModal(issuer)"
                  class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="별칭 추가">
                  <i class="fas fa-plus text-sm"></i>
                </button>
                <button @click="handleOpenEditModal(issuer)"
                  class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
                  title="발행사 수정">
                  <i class="fas fa-edit text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- 별칭 목록 (펼쳐진 상태에서만 표시) -->
          <div class="border-t border-gray-200 bg-gray-50 p-4">
            <div v-if="!issuer.bondAliases || issuer.bondAliases.length === 0" class="text-center py-4 text-gray-500">
              <i class="fas fa-tags text-xl mb-2"></i>
              <p class="text-sm">등록된 별칭이 없습니다.</p>
            </div>
            <div v-else class="space-y-2">
              <h4 class="text-sm font-medium text-gray-700 mb-3">별칭 목록</h4>
              <div class="space-y-2">
                <div v-for="alias in issuer.bondAliases" :key="alias.id"
                  class="flex items-center justify-between bg-white p-3 rounded border border-gray-200">
                  <span class="text-sm font-medium text-gray-900">{{ alias.name }}</span>
                  <button @click="confirmDeleteAlias(issuer.id, alias.id, alias.name)"
                    class="text-gray-400 hover:text-red-500 p-1 rounded transition-colors" title="별칭 삭제">
                    <i class="fas fa-trash text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 발행사 생성 모달 -->
    <BaseModal v-model="modals.create" title="발행사 추가" @close="handleCloseCreateModal">
      <div class="space-y-4">
        <BaseInput v-model="createForm.name" label="발행사명" placeholder="발행사명을 입력하세요" required />

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">크레딧 유형</label>
          <select v-model="createForm.creditType" class="select-base" required>
            <option value="">유형을 선택하세요</option>
            <option v-for="(name, type) in creditTypes" :key="type" :value="type">
              {{ name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">신용등급</label>
          <select v-model="createForm.grade" class="select-base" required>
            <option value="">신용등급을 선택하세요</option>
            <option v-for="rating in creditRatings" :key="rating" :value="rating">
              {{ rating }}
            </option>
          </select>
        </div>
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="handleCloseCreateModal">
          취소
        </BaseButton>
        <BaseButton variant="primary" @click="handleCreate" :loading="isCreating">
          추가
        </BaseButton>
      </template>
    </BaseModal>

    <!-- 발행사 수정 모달 -->
    <BaseModal v-model="modals.edit" title="발행사 수정" size="lg" @close="handleCloseEditModal">
      <div class="space-y-4">
        <BaseInput v-model="editForm.name" label="발행사명" required />

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">신용등급</label>
          <select v-model="editForm.grade" class="select-base" required>
            <option v-for="rating in creditRatings" :key="rating" :value="rating">
              {{ rating }}
            </option>
          </select>
        </div>

        <!-- 별칭 목록 -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">별칭 목록</label>
          <div v-if="!editForm.aliases || editForm.aliases.length === 0"
            class="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
            등록된 별칭이 없습니다.
          </div>
          <div v-else class="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
            <div v-for="alias in editForm.aliases" :key="alias.id"
              class="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span class="text-sm">{{ alias.name }}</span>
              <button @click="confirmDeleteAlias(selectedIssuer.id, alias.id, alias.name)"
                class="text-red-500 hover:text-red-700 text-xs">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="handleCloseEditModal">
          취소
        </BaseButton>
        <BaseButton variant="primary" @click="handleUpdate" :loading="isUpdating">
          저장
        </BaseButton>
      </template>
    </BaseModal>

    <!-- 별칭 추가 모달 -->
    <BaseModal v-model="modals.addAlias" title="별칭 추가" @close="handleCloseAddAliasModal">
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">발행사</label>
          <div class="p-3 bg-gray-50 rounded border text-sm font-medium">
            {{ selectedIssuer?.name }}
          </div>
        </div>

        <BaseInput v-model="aliasForm.name" label="별칭명" placeholder="별칭을 입력하세요" required />
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="handleCloseAddAliasModal">
          취소
        </BaseButton>
        <BaseButton variant="primary" @click="handleAddAlias" :loading="isAddingAlias">
          추가
        </BaseButton>
      </template>
    </BaseModal>

    <!-- 로딩 오버레이 -->
    <div v-if="isCreating || isUpdating || isAddingAlias || isDeletingAlias" class="loading-overlay">
      <div class="bg-white rounded-xl p-8 shadow-2xl">
        <div class="flex items-center space-x-4">
          <div class="loading-spinner"></div>
          <span class="text-gray-700 font-medium">처리 중...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseModal from '@/components/common/BaseModal.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import { useCreditIssuers } from '@/composables/useCreditIssuers';
import { useIssuerManagement } from '@/composables/useIssuerManagement';
import {
  useCreditManagement,
  useFormValidation,
  useErrorHandling
} from '@/composables/useCreditManagement';

export default {
  name: 'CreditIssuerComponent',
  components: {
    BaseButton,
    BaseModal,
    BaseInput,
  },
  props: {
    selectedDate: {
      type: String,
      required: true,
    },
  },

  setup() {
    const {
      isLoading,
      typeNames,
      issuersData,
      loadCreditData,
      createIssuer,
      updateIssuer,
      createAlias,
      deleteAlias,
    } = useCreditIssuers();

    const { creditRatings } = useCreditManagement();

    const {
      modals,
      selectedIssuer,
      getGradeColorClass,
      openModal,
      closeModal,
    } = useIssuerManagement();

    const { validateRequired, showValidationError } = useFormValidation();
    const { handleAsyncOperation } = useErrorHandling();

    // 로딩 상태
    const isCreating = ref(false);
    const isUpdating = ref(false);
    const isAddingAlias = ref(false);
    const isDeletingAlias = ref(false);

    // 현재 크레딧 타입
    const currentCreditType = ref('PUBLIC');

    // 폼 상태
    const createForm = ref({
      name: '',
      creditType: '',
      grade: '',
    });

    const editForm = ref({
      name: '',
      grade: '',
      aliases: [],
    });

    const aliasForm = ref({
      name: '',
    });

    // Computed
    const creditTypes = computed(() => typeNames.value || {});
    const issuers = computed(() => issuersData.value || {});
    const currentIssuers = computed(() => {
      const issuersList = issuers.value[currentCreditType.value] || [];
      // 가나다순으로 정렬 (한글 이름 기준)
      return issuersList.sort((a, b) => {
        return a.name.localeCompare(b.name, 'ko');
      });
    });

    // 모달 핸들러들
    const handleOpenCreateModal = () => {
      createForm.value = {
        name: '',
        creditType: currentCreditType.value,
        grade: '',
      };
      openModal('create');
    };

    const handleCloseCreateModal = () => {
      closeModal('create');
    };

    const handleOpenEditModal = (issuer) => {
      editForm.value = {
        name: issuer.name,
        grade: issuer.grade,
        aliases: issuer.bondAliases || [],
      };
      openModal('edit', issuer);
    };

    const handleCloseEditModal = () => {
      closeModal('edit');
    };

    const handleOpenAddAliasModal = (issuer) => {
      aliasForm.value.name = '';
      openModal('addAlias', issuer);
    };

    const handleCloseAddAliasModal = () => {
      closeModal('addAlias');
    };

    // CRUD 작업들
    const handleCreate = async () => {
      const validation = validateRequired(createForm.value, ['name', 'creditType', 'grade']);

      if (!validation.isValid) {
        showValidationError(validation.missingFields, {
          name: '발행사명',
          creditType: '크레딧 유형',
          grade: '신용등급'
        });
        return;
      }

      await handleAsyncOperation(
        async () => {
          await createIssuer({
            ...createForm.value,
            type: createForm.value.creditType
          });
          handleCloseCreateModal();
        },
        isCreating,
        '발행사 생성 중 오류가 발생했습니다.'
      );
    };

    const handleUpdate = async () => {
      const validation = validateRequired(editForm.value, ['name', 'grade']);

      if (!validation.isValid) {
        showValidationError(validation.missingFields, {
          name: '발행사명',
          grade: '신용등급'
        });
        return;
      }

      // selectedIssuer가 제대로 설정되었는지 확인
      if (!selectedIssuer.value || !selectedIssuer.value.id) {
        console.error('선택된 발행사 정보가 없습니다.');
        return;
      }

      await handleAsyncOperation(
        async () => {
          await updateIssuer(selectedIssuer.value.id, {
            name: editForm.value.name,
            grade: editForm.value.grade,
          });
          handleCloseEditModal();
        },
        isUpdating,
        '발행사 수정 중 오류가 발생했습니다.'
      );
    };

    const handleAddAlias = async () => {
      const validation = validateRequired(aliasForm.value, ['name']);

      if (!validation.isValid) {
        showValidationError(validation.missingFields, {
          name: '별칭명'
        });
        return;
      }

      // selectedIssuer가 제대로 설정되었는지 확인
      if (!selectedIssuer.value || !selectedIssuer.value.id) {
        console.error('선택된 발행사 정보가 없습니다.');
        return;
      }

      await handleAsyncOperation(
        async () => {
          await createAlias(selectedIssuer.value.id, aliasForm.value.name);
          handleCloseAddAliasModal();
        },
        isAddingAlias,
        '별칭 추가 중 오류가 발생했습니다.'
      );
    };

    const confirmDeleteAlias = (issuerId, aliasId, name) => {
      if (confirm(`'${name}' 별칭을 삭제하시겠습니까?`)) {
        handleDeleteAlias(issuerId, aliasId);
      }
    };

    const handleDeleteAlias = async (issuerId, aliasId) => {
      await handleAsyncOperation(
        async () => {
          await deleteAlias(issuerId, aliasId);
          // 수정 모달이 열려있다면 별칭 목록 업데이트
          if (modals.value.edit && selectedIssuer.value?.id === issuerId) {
            editForm.value.aliases = editForm.value.aliases.filter(alias => alias.id !== aliasId);
          }
        },
        isDeletingAlias,
        '별칭 삭제 중 오류가 발생했습니다.'
      );
    };

    onMounted(async () => {
      await loadCreditData();
    });

    return {
      // 데이터
      isLoading,
      creditTypes,
      issuers,
      currentCreditType,
      currentIssuers,
      creditRatings,

      // 모달 상태
      modals,
      selectedIssuer,
      isCreating,
      isUpdating,
      isAddingAlias,
      isDeletingAlias,

      // 폼 상태
      createForm,
      editForm,
      aliasForm,

      // UI 관리
      getGradeColorClass,

      // 모달 핸들러
      handleOpenCreateModal,
      handleCloseCreateModal,
      handleOpenEditModal,
      handleCloseEditModal,
      handleOpenAddAliasModal,
      handleCloseAddAliasModal,

      // CRUD 작업
      handleCreate,
      handleUpdate,
      handleAddAlias,
      confirmDeleteAlias,
    };
  },
};
</script>

<style scoped>
.select-base {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white;
}

.loading-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50;
}

.loading-spinner {
  @apply animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600;
}
</style>
